/**
 * The keyboard-shortcut sheet behind the toolbar's `?` button.
 *
 * Not a panel: it has no state, no store subscription and no place in a column — it is a modal
 * the user opens, reads and closes. It reuses the `.dlg` chrome of `../dialogs.ts` (same shape:
 * `h2` + `.dlg-body` + `.dlg-actions`) instead of inventing a second dialog look.
 *
 * The list below is generated from the code, not from the README: `viewport/mount.ts` for the
 * viewport keys, `timeline/shortcuts.ts` for the timeline keys, `app.ts` for the global ones.
 * If a binding moves, this file is wrong until it moves too.
 */

const GROUPS: ReadonlyArray<{
  title: string;
  rows: ReadonlyArray<readonly [string, string]>;
}> = [
  {
    title: 'Viewport',
    rows: [
      ['F', 'Frame the model'],
      ['1 / 3 / 7', 'Front / side / top view'],
      ['5', 'Perspective view'],
      ['R', 'Rotate gizmo (ANIMATE)'],
      ['G', 'Move joint (RIG)'],
    ],
  },
  {
    title: 'Timeline (ANIMATE)',
    rows: [
      ['Space', 'Play / pause'],
      ['K', 'Key the selected bone'],
      ['Shift + K', 'Key every posed bone'],
      ['Delete', 'Delete the selected keys (or event)'],
      ['Home / End', 'First / last frame'],
      ['← / →', 'Step one frame'],
      ['Shift + ← / →', 'Step ten frames'],
      [', / .', 'Previous / next keyframe'],
      ['L', 'Toggle loop'],
      ['A', 'Select every key of the selected bone'],
      ['Ctrl + C / V', 'Copy / paste keys at the playhead'],
    ],
  },
  {
    title: 'Global',
    rows: [
      ['Ctrl + Z', 'Undo'],
      ['Ctrl + Shift + Z', 'Redo'],
      ['Ctrl + Y', 'Redo'],
      ['Ctrl + S', 'Save clip'],
      ['Ctrl + Shift + S', 'Save clip as…'],
    ],
  },
];

/** The one open sheet. A second `?` click while it is up must not stack a second dialog. */
let current: HTMLDialogElement | null = null;

export function openHelpDialog(): void {
  if (typeof document === 'undefined') return;
  if (current?.isConnected) {
    current.focus();
    return;
  }

  const dialog = document.createElement('dialog');
  dialog.className = 'dlg help';
  dialog.dataset.dlgKind = 'help';

  const heading = document.createElement('h2');
  heading.textContent = 'Keyboard shortcuts';

  const body = document.createElement('div');
  body.className = 'dlg-body';
  for (const group of GROUPS) {
    const block = document.createElement('div');
    block.className = 'help-group';
    const title = document.createElement('h3');
    title.textContent = group.title;
    block.appendChild(title);
    for (const [keys, what] of group.rows) {
      const row = document.createElement('div');
      row.className = 'help-row';
      const kbd = document.createElement('kbd');
      kbd.textContent = keys;
      const label = document.createElement('span');
      label.textContent = what;
      row.append(kbd, label);
      block.appendChild(row);
    }
    body.appendChild(block);
  }

  const close = document.createElement('button');
  close.type = 'button';
  close.className = 'primary';
  close.dataset.dlg = 'close';
  close.textContent = 'Close';

  const actions = document.createElement('div');
  actions.className = 'dlg-actions';
  actions.appendChild(close);

  dialog.append(heading, body, actions);

  let backdrop: HTMLElement | null = null;
  let closed = false;

  const finish = (): void => {
    if (closed) return;
    closed = true;
    try {
      if (dialog.open && typeof dialog.close === 'function') dialog.close();
    } catch {
      /* a backend-less <dialog> (very old jsdom) has nothing to close */
    }
    dialog.remove();
    backdrop?.remove();
    if (current === dialog) current = null;
  };

  close.addEventListener('click', finish);

  /*
   * A modal owns the keyboard. EVERY key stops here, or Space plays the timeline and Delete eats
   * a keyframe behind the sheet — the app's shortcut handlers sit on `window`/`document` in the
   * bubble phase. Escape is handled here too: jsdom fires no native `cancel`.
   */
  dialog.addEventListener('keydown', (event) => {
    event.stopPropagation();
    if (event.key === 'Escape' || event.key === 'Enter') {
      event.preventDefault();
      finish();
    }
  });
  dialog.addEventListener('cancel', (event) => {
    event.preventDefault();
    finish();
  });
  dialog.addEventListener('close', finish);

  document.body.appendChild(dialog);
  current = dialog;

  let modal = false;
  try {
    if (typeof dialog.showModal === 'function') {
      dialog.showModal();
      modal = true;
    }
  } catch {
    /* jsdom: "Not implemented: HTMLDialogElement.prototype.showModal" */
  }
  if (!modal) {
    dialog.setAttribute('open', '');
    backdrop = document.createElement('div');
    backdrop.className = 'dlg-backdrop';
    dialog.parentNode?.insertBefore(backdrop, dialog);
  }

  close.focus();
}
